import { createSlice, PayloadAction } from '@reduxjs/toolkit'

const initialState: SeasonsSlice = {
  currentSeason: null,
  pastSeasons: [],
  currentSeasonProgress: null,
  pastSeasonsProgress: null,
  recentlyCompletedQuests: [],
  currentSeasonGraduatedMemecoins: [],
  claimPrizeTxHash: null,
  previousSeasonFocusingOn: null,
}

export type SerializedUserSeasonProgress = {
  [seasonId: string]: UserSeasonProgress
}

export type GraduatedMemecoin = {
  tokenName: string
  imgUrl: string
  tokenAddress: string
  ethPrice: string
  liquidityPoolAddress: string
}

export const seasons = createSlice({
  name: 'seasons',
  initialState,
  reducers: {
    setCurrentSeason: (state, { payload }: PayloadAction<Season>) => {
      state.currentSeason = payload
    },
    setPastSeasons: (state, { payload }: PayloadAction<Season[]>) => {
      state.pastSeasons = payload
    },
    setCurrentSeasonProgress: (
      state,
      { payload }: PayloadAction<UserSeasonProgress>,
    ) => {
      state.currentSeasonProgress = payload
    },
    setPastSeasonProgress: (
      state,
      { payload }: PayloadAction<{ [seasonId: string]: UserSeasonProgress }>,
    ) => {
      state.pastSeasonsProgress = payload
    },
    setRecentlyCompletedQuests: (
      state,
      { payload }: PayloadAction<RecentlyCompletedQuest[]>,
    ) => {
      state.recentlyCompletedQuests = payload
    },
    setCurrentSeasonGraduatedMemecoins: (
      state,
      { payload }: PayloadAction<GraduatedMemecoin[]>,
    ) => {
      state.currentSeasonGraduatedMemecoins = payload
    },
    setClaimPrizeTxHash: (state, { payload }: PayloadAction<string | null>) => {
      state.claimPrizeTxHash = payload
    },
    setPreviousSeasonFocusingOn: (state, { payload }: PayloadAction<Season>) => {
      state.previousSeasonFocusingOn = payload
    },
  }
})

export const {
  setCurrentSeason,
  setPastSeasons,
  setCurrentSeasonProgress,
  setPastSeasonProgress,
  setRecentlyCompletedQuests,
  setCurrentSeasonGraduatedMemecoins,
  setClaimPrizeTxHash,
  setPreviousSeasonFocusingOn
} = seasons.actions

export default seasons.reducer

export type RecentlyCompletedQuest = {
  questId: string
  oldProgress: string
}

// NOTE: Devs, I want to store the types in the slice. Think it keeps the codebase a bit more atomic
// that reducerTypes file is a fucking monstrosity :skull"

export enum PrizeType {
  ERC20 = 'ERC20',
  NFT = 'NFT',
  OFFCHAIN = 'OFFCHAIN',
}

export type SeasonPrize = {
  name: string
  symbol: string
  description?: string
  prizeType: PrizeType
  amount: number
  tokenAddress?: string
  tokenIds?: number[]
  uuid?: string
  imageUrl?: string
  nftImageUrls?: string[]
}

// NOTE: we can store our prizes in here, and if we need we can make relational
// at the momemnt I don't see the harm in just having it as an array in here
// we don't need to index by prizes or anything like that so let's keep it dynamic
export interface MetaData {
  [key: string]: any
  prizes: SeasonPrize[]
}

export interface Season {
  id?: string
  name: string
  description: string
  amountOfWinners: number
  startDate: Date
  endDate: Date
  winners: string[]
  contractAddress?: string
  videoUrl?: string
  meta_data: MetaData
  createdAt?: Date
  updatedAt?: Date
}

export type SeasonsSlice = {
  currentSeason: Season | null
  pastSeasons: Season[]
  currentSeasonProgress: UserSeasonProgress | null
  pastSeasonsProgress: { [seasonId: string]: UserSeasonProgress } | null
  recentlyCompletedQuests: RecentlyCompletedQuest[]
  currentSeasonGraduatedMemecoins: GraduatedMemecoin[]
  claimPrizeTxHash: string | null
  previousSeasonFocusingOn: Season | null
}

export interface QuestProgress {
  questId: string
  progress: string // this is a string so we can use wei value
  timesCompleted: number
  partialCompletionTickets?: number
  lastUpdated?: Date
}

export interface UserSeasonProgress {
  userId: number
  seasonId: string
  quests: QuestProgress[]
  tickets: number
  hasAcknowledgedStart?: boolean
  hasAcknowledgedEnd?: boolean
  poolsTradedInThisSeason: string[]
  hasWon?: boolean
  claimPrizeTxHash?: string
  createdAt?: Date
  updatedAt?: Date
}

export enum QuestType {
  Linear = 'linear',
  AllOrNothing = 'all_or_nothing',
}

export enum QuestObjective {
  TradeVolume = 'trade_volume',
  UniquePools = 'unique_pools',
  TotalTrades = 'total_trades',
  QuestCompletions = 'quest_completions',
}

export enum QuestDuration {
  Daily = 'daily',
  Seasonal = 'seasonal',
}

export interface IQuestProgressRequirement {
  totalProgressRequired: number
}

export interface IQuest {
  questId: string
  name: string
  description: string
  questType: QuestType
  duration: QuestDuration
  objective: QuestObjective
  maxTickets: number
  // keeping as a string as we will use bignumbers for ETH progress
  // we may want to do 4.2069 ETH or stupid shit too so good to just use
  // wei for any blockchain value requirements
  progressRequirement: string
  createdAt?: Date
  updatedAt?: Date
}
